/**
 * Empire-PWA chassis · voice. See almari/src/chassis/voice.ts for rationale.
 */

export interface RecogResult {
  readonly transcript: string;
  readonly isFinal: boolean;
  readonly confidence: number;
}

interface SpeechRecognitionLike extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionCtor {
  new (): SpeechRecognitionLike;
}

interface WindowWithSR extends Window {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

function getCtor(): SpeechRecognitionCtor | null {
  const w = window as WindowWithSR;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isAvailable(): boolean {
  return getCtor() !== null;
}

export async function* listen(lang: string = 'en-IN'): AsyncIterableIterator<RecogResult> {
  const Ctor = getCtor();
  if (!Ctor) throw new Error('speech recognition unavailable');
  const rec = new Ctor();
  rec.continuous = true;
  rec.interimResults = true;
  rec.lang = lang;

  const queue: RecogResult[] = [];
  let resolve: ((value: IteratorResult<RecogResult>) => void) | null = null;
  let done = false;

  rec.addEventListener('result', ((e: Event) => {
    const ev = e as unknown as { results: Iterable<Iterable<{ transcript: string; confidence: number }> & { isFinal: boolean }> };
    for (const r of ev.results) {
      const alt = [...r][0];
      if (!alt) continue;
      const item: RecogResult = { transcript: alt.transcript, isFinal: r.isFinal, confidence: alt.confidence };
      if (resolve) { resolve({ value: item, done: false }); resolve = null; }
      else queue.push(item);
    }
  }) as EventListener);

  rec.addEventListener('error', ((): void => { done = true; if (resolve) { resolve({ value: undefined, done: true }); resolve = null; } }) as EventListener);
  rec.addEventListener('end', ((): void => { done = true; if (resolve) { resolve({ value: undefined, done: true }); resolve = null; } }) as EventListener);

  rec.start();

  try {
    while (!done) {
      if (queue.length > 0) {
        yield queue.shift()!;
      } else {
        const p = new Promise<IteratorResult<RecogResult>>((res) => { resolve = res; });
        const r = await p;
        if (r.done) return;
        yield r.value;
      }
    }
  } finally {
    rec.abort();
  }
}

export async function speak(text: string, lang: string = 'en-IN'): Promise<void> {
  if (typeof speechSynthesis === 'undefined') return;
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = lang;
  utter.rate = 1.05;
  utter.pitch = 1;
  return new Promise((resolve) => {
    utter.addEventListener('end', () => resolve());
    utter.addEventListener('error', () => resolve());
    speechSynthesis.speak(utter);
  });
}

/**
 * MediaRecorder voice-memo helper. Encrypted-at-rest via OPFS + AES-GCM (caller responsible).
 */
export async function recordMemo(maxMs: number = 60_000): Promise<Blob> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const chunks: Blob[] = [];
  const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
  const rec = new MediaRecorder(stream, { mimeType: mime });
  return new Promise((resolve, reject) => {
    rec.addEventListener('dataavailable', (e) => { if (e.data.size) chunks.push(e.data); });
    rec.addEventListener('stop', () => {
      stream.getTracks().forEach((t) => t.stop());
      resolve(new Blob(chunks, { type: mime }));
    });
    rec.addEventListener('error', (e) => reject(e));
    rec.start();
    setTimeout(() => { if (rec.state === 'recording') rec.stop(); }, maxMs);
  });
}

export async function bulbulSynthesise(_text: string, _voice: string = 'meera'): Promise<Blob | null> {
  return null;
}
